/**
 * Error and uncertain-outcome feedback of the pricing screens (#3034).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { JournalStatus } from './pricingJournal';
import { describePricingError } from './pricingErrors';

/** A refusal or failure, in words the user can act on. */
export function PricingErrorAlert({ error }: { error: unknown }) {
  const { t } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  if (error === null || error === undefined) return null;
  return (
    <Alert severity="error" role="alert">
      {describePricingError(error, t, tc)}
    </Alert>
  );
}

interface UncertainOutcomeProps {
  /** The last answer of this screen's own attempt; `null` when the request was found again after a reload. */
  error: unknown;
  attempts: number;
  busy: boolean;
  onRetry: () => void;
  onDiscard: () => void;
  /** Extra sentence about what the request was (e.g. "creating a campaign"). */
  what: string;
  /** What the browser keeps of the request; `unavailable` says a reload cannot recover it. */
  journalStatus?: JournalStatus | undefined;
}

/**
 * The write may or may not have happened. The same request (payload and key) is held; retrying sends it
 * unchanged, and the server either replays its first answer or refuses a second run. Giving it up is
 * the user's explicit, confirmed choice, because a fresh request could duplicate the first.
 */
export function UncertainOutcomeAlert({ error, attempts, busy, onRetry, onDiscard, what, journalStatus }: UncertainOutcomeProps) {
  const { t } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const [confirming, setConfirming] = useState(false);
  return (
    <Alert severity="warning" role="alert">
      <AlertTitle>{t('uncertain.title')}</AlertTitle>
      <Stack spacing={1}>
        <span>{t('uncertain.body', { what })}</span>
        <span>{error === null || error === undefined ? t('uncertain.afterReload') : describePricingError(error, t, tc)}</span>
        {attempts > 1 && <span>{t('uncertain.attempts', { count: attempts })}</span>}
        {journalStatus === 'unavailable' && <span>{t('uncertain.storageUnavailable')}</span>}
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" color="warning" onClick={onRetry} disabled={busy}>
            {t('uncertain.retry')}
          </Button>
          <Button size="small" onClick={() => setConfirming(true)} disabled={busy}>
            {t('uncertain.discard')}
          </Button>
        </Stack>
      </Stack>
      <ConfirmDialog
        open={confirming}
        title={t('uncertain.discardTitle')}
        message={t('uncertain.discardWarning', { what })}
        confirmText={t('uncertain.discardConfirm')}
        confirmColor="warning"
        onConfirm={() => {
          setConfirming(false);
          onDiscard();
        }}
        onCancel={() => setConfirming(false)}
      />
    </Alert>
  );
}

interface JournalStatusProps {
  status: JournalStatus;
  /** Whether a request is held right now (then its own alert already says the storage is unavailable). */
  holding: boolean;
  onDiscardDamaged: () => void;
}

/**
 * The browser's record of earlier requests could not be kept or read. Unreadable: nothing new starts until the user knowingly
 * discards it (an earlier request cannot be ruled out). Unavailable: requests work, but a reload cannot recover one.
 */
export function JournalStatusNotice({ status, holding, onDiscardDamaged }: JournalStatusProps) {
  const { t } = useTranslation('partPricing');
  const [confirming, setConfirming] = useState(false);
  if (status === 'corrupt') {
    return (
      <Alert severity="error" role="alert">
        <AlertTitle>{t('journal.corruptTitle')}</AlertTitle>
        <Stack spacing={1}>
          <span>{t('journal.corruptBody')}</span>
          <Stack direction="row">
            <Button size="small" color="error" onClick={() => setConfirming(true)}>
              {t('journal.corruptDiscard')}
            </Button>
          </Stack>
        </Stack>
        <ConfirmDialog
          open={confirming}
          title={t('journal.corruptDiscardTitle')}
          message={t('journal.corruptDiscardWarning')}
          confirmText={t('journal.corruptDiscardConfirm')}
          confirmColor="error"
          onConfirm={() => {
            setConfirming(false);
            onDiscardDamaged();
          }}
          onCancel={() => setConfirming(false)}
        />
      </Alert>
    );
  }
  if (status === 'unavailable' && !holding) {
    return <Alert severity="warning">{t('journal.unavailable')}</Alert>;
  }
  return null;
}
